import { createAsyncThunk } from '@reduxjs/toolkit'
import { deserialize, serialize } from '../utility/serialization'

import Axios from '../axios'
import { handleAPIError } from '../utility/actionHelper'

// THUNKS

export const getEnrollmentData = createAsyncThunk(
  'programRegistration/getEnrollmentData',
  async ({ courseId, invitationToken }, { rejectWithValue }) => {
    try {
      const { data } = await requestEnrollmentData(courseId, invitationToken)
      return deserialize(data)
    } catch (error) {
      return handleAPIError(error, rejectWithValue)
    }
  }
)

// Same request as getEnrollmentData, but for a screen that is already open: the family changed under it.
export const refreshEnrollmentData = createAsyncThunk(
  'programRegistration/refreshEnrollmentData',
  async ({ courseId }, { getState, rejectWithValue }) => {
    try {
      const { invitationToken } = getState().programRegistration
      const { data } = await requestEnrollmentData(courseId, invitationToken)

      return deserialize(data)
    } catch (error) {
      return handleAPIError(error, rejectWithValue)
    }
  }
)

export const requestEnrollment = createAsyncThunk(
  'programRegistration/requestsEnrollment',
  async ({ courseId, userId, packageId, packagePrice, materialsIds }, { getState, rejectWithValue }) => {
    try {
      const { invitationToken } = getState().programRegistration
      const { data } = await requestRequestEnrollment(courseId, userId, packageId, packagePrice, materialsIds, invitationToken)
      return deserialize(data)
    } catch (error) {
      return handleAPIError(error, rejectWithValue)
    }
  }
)

export const initializePayment = createAsyncThunk(
  'programRegistration/initializePayment',
  async ({ courseId, userId, packageId, packagePrice, materialsIds }, { getState, rejectWithValue }) => {
    try {
      const { invitationToken } = getState().programRegistration
      const { data } = await requestInitializePayment(courseId, userId, packageId, packagePrice, materialsIds, invitationToken)
      return deserialize(data)
    } catch (error) {
      return handleAPIError(error, rejectWithValue)
    }
  }
)

export const quotePayment = createAsyncThunk(
  'programRegistration/quotePayment',
  async ({ registrationId, paymentMethod, membershipPlanId }, { getState, rejectWithValue }) => {
    try {
      const billingAddress = getState().programRegistration.paymentSession?.billingAddress
      const { data } = await requestQuotePayment(registrationId, paymentMethod, membershipPlanId, billingAddress)
      return deserialize(data)
    } catch (error) {
      return handleAPIError(error, rejectWithValue)
    }
  }
)

export const completeProgramFeePayment = createAsyncThunk(
  'programRegistration/completeProgramFeePayment',
  async ({ registrationId, paymentMethodId }, { rejectWithValue }) => {
    try {
      const { data } = await requestCompleteProgramFeePayment(registrationId, paymentMethodId)
      return deserialize(data)
    } catch (error) {
      return handleAPIError(error, rejectWithValue)
    }
  }
)

export const completeMembershipPayment = createAsyncThunk(
  'programRegistration/completeMembershipPayment',
  async ({ registrationId }, { rejectWithValue }) => {
    try {
      const { data } = await requestCompleteMembershipPayment(registrationId)
      return deserialize(data)
    } catch (error) {
      return handleAPIError(error, rejectWithValue)
    }
  }
)

export const completeOfflineRegistration = createAsyncThunk(
  'programRegistration/completeOfflineRegistration',
  async ({ registrationId }, { rejectWithValue }) => {
    try {
      const { data } = await requestCompleteOfflineRegistrationService(registrationId)
      return deserialize(data)
    } catch (error) {
      return handleAPIError(error, rejectWithValue)
    }
  }
)

export const getRegistration = createAsyncThunk(
  'programRegistration/getRegistration',
  async ({ registrationId }, { rejectWithValue }) => {
    try {
      const { data } = await requestGetRegistration(registrationId)
      return deserialize(data)
    } catch (error) {
      return handleAPIError(error, rejectWithValue)
    }
  }
)


// API CALLS

function requestEnrollmentData(courseId, invitationToken) {
  const requestUrl = '/api/v1/enrollments/request_enrollment_data'
  return Axios.get(requestUrl, { params: serialize({ courseId, invitationToken }) })
}

// eslint-disable-next-line max-params
function requestRequestEnrollment(courseId, userId, packageId, packagePrice, materialsIds, invitationToken) {
  const requestUrl = '/api/v1/enrollments/request_enrollment'
  const embed = localStorage?.getItem('embed')
  const requestEnrollmentData = { userId, packageId, packagePrice, embed, materialsIds, invitationToken }

  return Axios.post(requestUrl, serialize(requestEnrollmentData), { params: serialize({ courseId }) })
}

// eslint-disable-next-line max-params
function requestInitializePayment(courseId, userId, packageId, packagePrice, materialsIds, invitationToken) {
  const requestUrl = '/api/v1/enrollments/initialize_payment'
  const embed = localStorage?.getItem('embed')
  const initializePaymentData = { userId, packageId, packagePrice, embed, materialsIds, invitationToken }

  return Axios.post(requestUrl, serialize(initializePaymentData), { params: serialize({ courseId }) })
}

// billing_address goes over as it comes off the Address Element: serialize would turn line1 into line_1
function requestQuotePayment(registrationId, paymentMethod, membershipPlanId, billingAddress) {
  const requestUrl = '/api/v1/enrollments/quote_payment'

  return Axios.post(requestUrl, { ...serialize({ registrationId, paymentMethod, membershipPlanId }), billing_address: billingAddress })
}

// the card id exists only when the form is mounted on the platform, and the server clones it only when it is sent
function requestCompleteProgramFeePayment(registrationId, paymentMethodId) {
  const requestUrl = '/api/v1/enrollments/complete_program_fee_payment'

  return Axios.post(requestUrl, serialize({ registrationId, ...(paymentMethodId && { paymentMethodId }) }))
}

function requestCompleteMembershipPayment(registrationId) {
  const requestUrl = '/api/v1/enrollments/complete_membership_payment'

  return Axios.post(requestUrl, serialize({ registrationId }))
}

function requestCompleteOfflineRegistrationService(registrationId) {
  const requestUrl = '/api/v1/enrollments/complete_offline_registration'

  return Axios.post(requestUrl, serialize({ registrationId }))
}

function requestGetRegistration(registrationId) {
  const requestUrl = `/api/v1/registrations/${registrationId}`

  return Axios.get(requestUrl)
}
